import React, { useState } from 'react';
import PageHero from '../../components/public/PageHero';
import PublicLayout from '../../components/public/PublicLayout';
import { PHONE_DISPLAY, SUPPORT_EMAIL, WHATSAPP_NUMBER } from '../../publicSite/config';
import { usePageMeta } from '../../publicSite/usePageMeta';

const enquiryTypes = [
  'Admissions / Registration',
  'Courses & Faculties',
  'Technical Support (Portal help)',
  'Partnerships / Sponsorship',
  'General'
];

const subjectOptions = {
  'Admissions / Registration': ['Admissions requirements', 'Cohort start dates', 'Application status'],
  'Courses & Faculties': ['Course selection', 'Faculty information', 'Learning outcomes'],
  'Technical Support (Portal help)': ['Login issues', 'Portal access', 'Assignments'],
  'Partnerships / Sponsorship': ['Sponsor a student', 'Corporate partnership', 'Receipts'],
  General: ['General enquiry']
};

const initialForm = {
  name: '',
  email: '',
  phone: '',
  enquiryType: enquiryTypes[0],
  subject: '',
  message: '',
  consent: false
};

const validate = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Enter your full name.';
  if (!/\S+@\S+\.\S+/.test(form.email)) errors.email = 'Enter a valid email address.';
  if (!form.phone.trim()) errors.phone = 'Enter your phone number.';
  if (!form.enquiryType) errors.enquiryType = 'Choose an enquiry type.';
  if (!form.subject) errors.subject = 'Choose a subject.';
  if (!form.message.trim()) errors.message = 'Tell us how we can help.';
  if (!form.consent) errors.consent = 'Consent is required before submitting.';
  return errors;
};

const readEnquiries = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('shem_admissions_enquiries') || '[]');
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
};

export default function AdmissionsEnquire() {
  usePageMeta('Admissions Enquiry | School of Shem', 'Send an admissions enquiry to School of Shem.');
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const update = (field) => (event) => {
    const value = field === 'consent' ? event.target.checked : event.target.value;
    setForm((previous) => {
      const next = { ...previous, [field]: value };
      if (field === 'enquiryType') next.subject = '';
      return next;
    });
    setErrors((previous) => ({ ...previous, [field]: '', ...(field === 'enquiryType' ? { subject: '' } : {}) }));
    setSuccess(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    const payload = { ...form, submittedAt: new Date().toISOString() };
    localStorage.setItem('shem_admissions_enquiries', JSON.stringify([...readEnquiries(), payload]));
    setSuccess(true);
    const enquiry = [
      'School of SHEM Admissions Enquiry',
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone}`,
      `Type: ${form.enquiryType}`,
      `Subject: ${form.subject}`,
      `Message: ${form.message}`
    ].join('\n');
    window.open(`https://wa.me/${String(WHATSAPP_NUMBER).replace(/\D/g, '')}?text=${encodeURIComponent(enquiry)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <PublicLayout>
      <PageHero
        eyebrow="Admissions"
        title="Make an enquiry"
        subtitle="Tell us what you need and the School of SHEM team will guide you toward the right next step."
        image="/images/shem-mentorship.jpg"
        imageAlt="A mentor providing thoughtful guidance to a prospective learner"
      />

      <section className="ps-section">
        <div className="ps-section-inner public-contact-layout">
          <div>
            <div className="ps-hero-eyebrow">Admissions support</div>
            <h2 className="ps-section-title">Clear guidance, without the complexity.</h2>
            <p className="ps-section-copy">
              Ask about applications, course selection, intake dates, payments, or access to the student portal.
            </p>
            <dl className="public-contact-details">
              <div>
                <dt>Email</dt>
                <dd><a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>{PHONE_DISPLAY}</dd>
              </div>
              <div>
                <dt>Response time</dt>
                <dd>Within 24–48 hours</dd>
              </div>
            </dl>
          </div>

          <div className="public-enquiry-panel">
            <h2>Enquiry details</h2>
            <p>All fields are required.</p>
            {success && (
              <div className="public-form-success" role="status">
                Your enquiry has been prepared. Continue in WhatsApp to send it to the admissions team.
              </div>
            )}
            <form className="public-form" onSubmit={handleSubmit} noValidate>
              <div className="public-form-grid">
                <label>
                  <span>Full name</span>
                  <input
                    aria-describedby={errors.name ? 'admissions-name-error' : undefined}
                    autoComplete="name"
                    value={form.name}
                    onChange={update('name')}
                    aria-invalid={Boolean(errors.name)}
                  />
                  {errors.name && <small id="admissions-name-error">{errors.name}</small>}
                </label>
                <label>
                  <span>Email address</span>
                  <input
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={update('email')}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? 'admissions-email-error' : undefined}
                  />
                  {errors.email && <small id="admissions-email-error">{errors.email}</small>}
                </label>
              </div>
              <label>
                <span>Phone / WhatsApp</span>
                <input
                  type="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={update('phone')}
                  aria-invalid={Boolean(errors.phone)}
                  aria-describedby={errors.phone ? 'admissions-phone-error' : undefined}
                />
                {errors.phone && <small id="admissions-phone-error">{errors.phone}</small>}
              </label>
              <label>
                <span>Enquiry type</span>
                <select value={form.enquiryType} onChange={update('enquiryType')}>
                  {enquiryTypes.map((type) => <option key={type}>{type}</option>)}
                </select>
              </label>
              <label>
                <span>Subject</span>
                <select
                  value={form.subject}
                  onChange={update('subject')}
                  aria-invalid={Boolean(errors.subject)}
                  aria-describedby={errors.subject ? 'admissions-subject-error' : undefined}
                >
                  <option value="">Choose a subject</option>
                  {(subjectOptions[form.enquiryType] || []).map((subject) => (
                    <option key={subject}>{subject}</option>
                  ))}
                </select>
                {errors.subject && <small id="admissions-subject-error">{errors.subject}</small>}
              </label>
              <label>
                <span>Message</span>
                <textarea
                  rows="6"
                  value={form.message}
                  onChange={update('message')}
                  aria-invalid={Boolean(errors.message)}
                  aria-describedby={errors.message ? 'admissions-message-error' : undefined}
                />
                {errors.message && <small id="admissions-message-error">{errors.message}</small>}
              </label>
              <label className="public-consent">
                <input type="checkbox" checked={form.consent} onChange={update('consent')} aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? 'admissions-consent-error' : undefined} />
                <span>I agree to be contacted about this enquiry.</span>
              </label>
              {errors.consent && <small className="public-field-error" id="admissions-consent-error">{errors.consent}</small>}
              <button className="btn btn-primary btn-lg" type="submit">Submit Enquiry</button>
            </form>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
